import { useEffect, useState } from "react";
import { usePlan } from "../context/PlanContext";
import Navbar from "./Navbar";
import UpgradeModal from "./UpgradeModal";
import PaymentUnavailableDialog from "./PaymentUnavailableDialog";
import { toast } from "react-toastify";
import "../Css/Billing.css";
import "../Css/PlansDialog.css";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

function formatCap(n) {
  if (n === -1) return "Unlimited";
  if (n == null || Number.isNaN(Number(n))) return "—";
  return String(n);
}

function catalogPlans(raw) {
  return [...(raw || [])]
    .filter((p) => {
      if (p.isListed === false) return false;
      const n = String(p.planName || "").toLowerCase();
      return (
        n === "starter" ||
        n === "advance" ||
        n === "custom" ||
        p.isCustom === true
      );
    })
    .sort((a, b) => {
      const rank = (p) => {
        const n = String(p.planName || "").toLowerCase();
        if (n === "starter") return 1;
        if (n === "advance") return 2;
        if (n === "custom" || p.isCustom) return 3;
        return 4;
      };
      return rank(a) - rank(b);
    });
}

function isCurrentPlan(plan, p) {
  if (!plan || !p) return false;
  const currentName = String(plan.planName || "").toLowerCase();
  const pName = String(p.planName || "").toLowerCase();
  const onCustom = plan.isCustom === true || currentName === "custom";
  if (onCustom) return p.isCustom === true || pName === "custom";
  if (plan.planNumber != null && p.planNumber != null) {
    return Number(p.planNumber) === Number(plan.planNumber);
  }
  return !!currentName && pName === currentName;
}

function statusClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "paid") return "paid";
  if (s === "pending") return "pending";
  if (s === "failed" || s === "overdue") return "overdue";
  return "";
}

export default function Billing() {
  const { billing, loading, refreshBilling, openUpgradeModal } = usePlan();
  const [payingId, setPayingId] = useState(null);
  const [paymentUnavailableOpen, setPaymentUnavailableOpen] = useState(false);
  const adminEmail =
    sessionStorage.getItem("adminEmail") ||
    sessionStorage.getItem("userEmail");

  useEffect(() => {
    refreshBilling();
  }, [refreshBilling]);

  const plan = billing?.plan;
  const usage = billing?.usage;
  const plans = catalogPlans(billing?.plans);

  const usagePct =
    usage?.freeLimit > 0
      ? Math.min(100, (100 * (usage?.used || 0)) / usage.freeLimit)
      : usage?.freeLimit === -1
        ? 8
        : 0;

  const payInvoice = async (invoice) => {
    if (!billing?.razorpayConfigured) {
      setPaymentUnavailableOpen(true);
      return;
    }

    setPayingId(invoice._id);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error("Failed to load Razorpay");
      const res = await fetch(`/api/billing/invoice/${invoice._id}/create-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ adminEmail }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 503 || /not configured/i.test(data.message || "")) {
          setPaymentUnavailableOpen(true);
          return;
        }
        throw new Error(data.message || "Could not start payment");
      }

      const rzp = new window.Razorpay({
        key: data.keyId,
        amount: data.amount,
        currency: data.currency || "INR",
        name: "Zordigo",
        description: `Invoice ${invoice.month}`,
        order_id: data.razorpayOrderId,
        handler: async (response) => {
          const verify = await fetch("/api/billing/verify", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              ...response,
              type: "overage_invoice",
              invoiceId: invoice._id,
              adminEmail,
            }),
          });
          const v = await verify.json();
          if (!verify.ok) {
            toast.error(v.message || "Verification failed");
            return;
          }
          toast.success("Invoice paid");
          refreshBilling();
        },
      });
      rzp.open();
    } catch (err) {
      if (/not configured|Failed to load Razorpay/i.test(err.message || "")) {
        setPaymentUnavailableOpen(true);
      } else {
        toast.error(err.message || "Payment failed");
      }
    } finally {
      setPayingId(null);
    }
  };

  return (
    <div className="billing-page">
      <Navbar />
      <UpgradeModal />
      <PaymentUnavailableDialog
        open={paymentUnavailableOpen}
        actionLabel="invoice payment"
        onClose={() => setPaymentUnavailableOpen(false)}
      />

      <div className="billing-shell">
        <header className="billing-header">
          <div>
            <h1>Billing &amp; Plan</h1>
            <p>Manage your subscription, usage, and invoices.</p>
          </div>
          {!plan?.isCustom && (
            <button
              type="button"
              className="billing-btn"
              onClick={() => openUpgradeModal(billing)}
            >
              Upgrade plan
            </button>
          )}
        </header>

        {loading && !billing ? (
          <div className="billing-loading">Loading billing…</div>
        ) : (
          <>
            {billing?.razorpayConfigured === false && (
              <div className="billing-banner warn">
                Online payments are not fully integrated yet. Plan limits still
                apply — for upgrades or invoice settlement, contact Zordigo
                support.
              </div>
            )}

            {billing?.usageBlocked && (
              <div className="billing-banner danger">
                Account locked due to unpaid overage invoice. Pay below to unlock
                {billing?.razorpayConfigured === false
                  ? " (online pay coming soon — contact support for now)."
                  : "."}
              </div>
            )}

            <section className="billing-summary">
              <article className="billing-summary-card">
                <p className="billing-kicker">Current plan</p>
                <h2>{plan?.planName || "—"}</h2>
                <div className="billing-stat-grid">
                  <div>
                    <span>Drivers</span>
                    <strong>{formatCap(plan?.allowedDriver)}</strong>
                  </div>
                  <div>
                    <span>Packers</span>
                    <strong>{formatCap(plan?.allowedPacker)}</strong>
                  </div>
                  <div>
                    <span>Dispatchers</span>
                    <strong>{formatCap(plan?.allowedDispatcher)}</strong>
                  </div>
                  <div>
                    <span>Orders / month</span>
                    <strong>{formatCap(plan?.freeOrdersPerMonth)}</strong>
                  </div>
                </div>
                <ul className="billing-feature-pills">
                  <li>
                    Live: {plan?.liveTrackingEnabled ? "Yes" : "No"}
                  </li>
                  <li>
                    History:{" "}
                    {plan?.historyDays === -1
                      ? "Unlimited"
                      : plan?.historyDays === 0
                        ? "None"
                        : `${plan?.historyDays} days`}
                  </li>
                  <li>
                    POD:{" "}
                    {plan?.podDays === -1
                      ? "Unlimited"
                      : plan?.podDays === 1
                        ? "Today only"
                        : `${plan?.podDays} days`}
                  </li>
                </ul>
              </article>

              <article className="billing-summary-card">
                <p className="billing-kicker">Usage this month</p>
                <h2>{usage?.month || "—"}</h2>
                <div className="billing-meter">
                  <div
                    className={`billing-meter-fill ${
                      usage?.warnLevel === "at_limit" ||
                      usage?.warnLevel === "over_limit"
                        ? "over"
                        : usage?.warnLevel === "approaching"
                          ? "warn"
                          : ""
                    }`}
                    style={{ width: `${usagePct}%` }}
                  />
                </div>
                <p className="billing-usage-text">
                  <strong>{usage?.used ?? 0}</strong>
                  {usage?.freeLimit >= 0
                    ? ` / ${usage.freeLimit} orders this month`
                    : " orders (unlimited)"}
                </p>
                {usage?.warnLevel === "approaching" && (
                  <p className="billing-warn">
                    You are nearing your monthly order limit.
                  </p>
                )}
                {(usage?.warnLevel === "at_limit" ||
                  usage?.warnLevel === "over_limit" ||
                  (usage?.freeLimit > 0 && usage?.used >= usage?.freeLimit)) && (
                  <p className="billing-warn">
                    Monthly order limit reached. New creates are blocked until
                    next month or you upgrade.
                  </p>
                )}
              </article>
            </section>

            <section className="billing-card">
              <div className="billing-card-head">
                <div>
                  <h2>Available plans</h2>
                  <p>Compare tiers and see what&apos;s included.</p>
                </div>
              </div>
              <div className="plans-dialog-grid billing-plans-grid">
                {plans.map((p) => {
                  const current = isCurrentPlan(plan, p);
                  const name = String(p.planName || "").toLowerCase();
                  const hist =
                    typeof p.historyDays === "number"
                      ? p.historyDays
                      : typeof p.liveTrackingDays === "number"
                        ? p.liveTrackingDays
                        : 0;
                  return (
                    <article
                      key={p._id || p.planNumber}
                      className={`plans-dialog-card ${current ? "current" : ""}`}
                    >
                      <div className="plans-dialog-card-top">
                        <div className="plans-dialog-card-name-row">
                          <h3>{p.planName}</h3>
                          {current && (
                            <span className="plans-dialog-badge">Selected</span>
                          )}
                        </div>
                        <div className="plans-dialog-price">
                          {p.isCustom || name === "custom" ? (
                            <>
                              <span className="plans-dialog-price-main">
                                Custom
                              </span>
                              <span className="plans-dialog-price-sub">
                                Pricing on request
                              </span>
                            </>
                          ) : name === "starter" || p.charges === "0" ? (
                            <>
                              <span className="plans-dialog-price-main">
                                Free
                              </span>
                              <span className="plans-dialog-price-sub">
                                Forever
                              </span>
                            </>
                          ) : (
                            <>
                              <span className="plans-dialog-price-main">
                                ₹{p.charges}
                              </span>
                              <span className="plans-dialog-price-sub">
                                {p.trialDays > 0
                                  ? `/month · ${p.trialDays}-day free trial`
                                  : "/month"}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <dl className="plans-dialog-features">
                        <div className="plans-dialog-feature">
                          <dt>Drivers</dt>
                          <dd>{formatCap(p.allowedDriver)}</dd>
                        </div>
                        <div className="plans-dialog-feature">
                          <dt>Packers</dt>
                          <dd>{formatCap(p.allowedPacker)}</dd>
                        </div>
                        <div className="plans-dialog-feature">
                          <dt>Dispatchers</dt>
                          <dd>{formatCap(p.allowedDispatcher)}</dd>
                        </div>
                        <div className="plans-dialog-feature">
                          <dt>Orders / month</dt>
                          <dd>{formatCap(p.freeOrdersPerMonth)}</dd>
                        </div>
                        <div className="plans-dialog-feature">
                          <dt>Live tracking</dt>
                          <dd>{p.liveTrackingEnabled ? "Yes" : "No"}</dd>
                        </div>
                        <div className="plans-dialog-feature">
                          <dt>History</dt>
                          <dd>
                            {hist === -1
                              ? "Unlimited"
                              : hist === 0
                                ? "None"
                                : `${hist} days`}
                          </dd>
                        </div>
                        <div className="plans-dialog-feature">
                          <dt>POD</dt>
                          <dd>
                            {p.podDays === -1
                              ? "Unlimited"
                              : p.podDays === 1
                                ? "Today only"
                                : p.podDays === 365
                                  ? "1 year"
                                  : `${p.podDays ?? 0} days`}
                          </dd>
                        </div>
                      </dl>
                    </article>
                  );
                })}
              </div>
            </section>

            <section className="billing-card">
              <div className="billing-card-head">
                <div>
                  <h2>Invoices</h2>
                  <p>Monthly overage invoices and payment status.</p>
                </div>
              </div>
              {(billing?.invoices || []).length === 0 ? (
                <div className="billing-empty">
                  No invoices yet. Overage charges appear here at month-end.
                </div>
              ) : (
                <div className="billing-table-wrap">
                  <table className="billing-table">
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th>Orders</th>
                        <th>Billable</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {billing.invoices.map((inv) => (
                        <tr key={inv._id}>
                          <td>{inv.month}</td>
                          <td>{inv.orderCount}</td>
                          <td>{inv.billableOrders}</td>
                          <td>₹{inv.amount}</td>
                          <td>
                            <span
                              className={`billing-status ${statusClass(
                                inv.status,
                              )}`}
                            >
                              {inv.status}
                            </span>
                          </td>
                          <td>
                            {inv.status === "pending" && (
                              <button
                                type="button"
                                className="billing-btn small"
                                disabled={payingId === inv._id}
                                onClick={() => payInvoice(inv)}
                              >
                                {payingId === inv._id ? "Opening…" : "Pay"}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}
